import React, {useEffect, useState} from 'react';
import {claimDailyBonus} from "../../services/bonus";
import {useSnackbar} from "../../context/SnackbarContext";
import {parseLaunchParams} from "../../utils/launch_params";
import BonusStore from '../../store/bonus';

const HINT_META = {
    time_boost: {
        title: "Time Boost",
        description: "+20 seconds to timer",
    },
    letters_reroll: {
        title: "Letter Shuffle",
        description: "Shuffle current letters",
    },
};

const BADGE_CLASSES = "inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold tracking-wide bg-emerald-700 text-white border border-emerald-800";

const DailyBonus = () => {
    const [streak, setStreak] = useState(0);
    const [rewardInfo, setRewardInfo] = useState({
        coins: 0,
        hintRewards: {},
        description: "",
        alreadyClaimed: false,
    });
    const {showSuccessSnackbar} = useSnackbar();
    const params = parseLaunchParams();

    useEffect(() => {
        if (!params.tg_user_id) {
            return;
        }

        claimDailyBonus()
            .then(response => {
                const {
                    streak_days,
                    coins_awarded,
                    hint_rewards,
                    reward_description,
                    last_reward_coins,
                    last_reward_hint_rewards,
                    already_claimed,
                } = response.data.response;

                const grantedHintRewards = hint_rewards || {};
                const hasGrantedHints = Object.values(grantedHintRewards).some(amount => amount > 0);
                const totalGrantedHints = Object.values(grantedHintRewards).reduce((sum, amount) => sum + amount, 0);
                // Формируем список названий подсказок для снекбара.
                const grantedHintTitles = Object.entries(grantedHintRewards)
                    .filter(([, amount]) => amount > 0)
                    .map(([hintType]) => (HINT_META[hintType]?.title || "Hint"));

                const displayCoins = already_claimed ? 0 : (last_reward_coins ?? coins_awarded);
                const lastHintRewards = last_reward_hint_rewards || {};
                // Показываем подсказки только в момент их получения (первый заход в сутки).
                const displayHintRewards = already_claimed
                    ? {}
                    : hasGrantedHints
                        ? grantedHintRewards
                        : Object.values(lastHintRewards).some(amount => amount > 0)
                            ? lastHintRewards
                            : {};

                const description = reward_description || (displayCoins > 0 ? `${displayCoins} coins` : "Random reward");

                setStreak(streak_days);
                setRewardInfo({
                    coins: displayCoins,
                    hintRewards: displayHintRewards,
                    description,
                    alreadyClaimed: Boolean(already_claimed),
                });

                const hasReward = coins_awarded > 0 || hasGrantedHints;
                if (hasReward) {
                    const rewardMessage = hasGrantedHints
                        ? totalGrantedHints > 1
                            ? `You received ${totalGrantedHints} hints: ${grantedHintTitles.join(', ')}`
                            : `You received 1 hint: ${grantedHintTitles[0]}`
                        : reward_description
                            ? `You received: ${reward_description}`
                            : coins_awarded > 0
                                ? `You received ${coins_awarded} ${coins_awarded === 1 ? 'coin' : 'coins'}`
                                : "You received a random reward";
                    showSuccessSnackbar(rewardMessage);
                }

                if (coins_awarded > 0) {
                    BonusStore.debit(coins_awarded);
                }
            })
            .catch(error => {
                console.error("Error claiming daily bonus", error);
            });
    }, [params.tg_user_id]);

    const streakText = streak > 0 ? `Current streak: ${streak} ${streak === 1 ? 'day' : 'days'}` : "Come back tomorrow to start your streak!";
    const icon = streak > 1 ? '🔥' : '🎁';
    const hintEntries = Object.entries(rewardInfo.hintRewards || {}).filter(([, amount]) => amount > 0);

    return (
        <div className="w-full p-4 bg-gray-800 rounded-xl flex flex-col">
            <div className="flex items-center justify-between">
                <div className="flex items-center">
                    <div className="text-3xl mr-4">{icon}</div>
                    <div>
                        <p className="font-bold text-gray-400">Daily bonus</p>
                        <p className="text-sm text-gray-500">{streakText}</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-7 gap-2 mt-4">
                {Array.from({length: 7}).map((_, index) => {
                    const dayNumber = index + 1;
                    const isCompleted = dayNumber <= streak;
                    const stateClasses = isCompleted ? "bg-orange-400" : "bg-gray-200";

                    return (
                        <div key={dayNumber}
                             className={`w-full h-2 rounded-md transition-all duration-300 ${stateClasses}`}/>
                    );
                })}
            </div>

            <div className="grid grid-cols-1 gap-3 mt-4">
                {rewardInfo.coins > 0 && (
                    <div className="relative bg-gray-700 rounded-xl p-4 shadow-sm border border-gray-800">
                        <div className="flex items-start justify-between gap-4 text-gray-400">
                            <div>
                                <p className="text-sm font-semibold">Bonus coins</p>
                                <p className="text-xs mt-1">{`${rewardInfo.coins} coins`}</p>
                            </div>
                            <span className={BADGE_CLASSES}>{`+${rewardInfo.coins}`}</span>
                        </div>
                    </div>
                )}
                {hintEntries.length > 0 && (
                    <div className="grid grid-cols-1 gap-3">
                        {hintEntries.map(([hintType, count]) => {
                            const meta = HINT_META[hintType] || {title: "Hint", description: "Reward detail"};
                            return (
                                <div key={hintType}
                                     className="relative bg-gray-700 rounded-xl p-4 shadow-sm border border-gray-800">
                                    <div className="flex items-start justify-between gap-4 text-gray-400">
                                        <div>
                                            <p className="text-sm font-semibold">Hint: {meta.title}</p>
                                            <p className="text-xs mt-1">{meta.description}</p>
                                        </div>
                                        <span className={BADGE_CLASSES}>{`+${count}`}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DailyBonus;
